import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import $api from 'http'
import Notification from 'utils/Notifications'
import {
  PageHeader,
  UnvButton,
  UnvInput,
  Skeleton,
  EmptyState,
  ErrorState,
  Badge,
  DataTable,
} from 'components'

import styles from './GradebookPage.module.scss'

const fmtDate = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('ru-RU')
  } catch {
    return '—'
  }
}

const GradebookPage = () => {
  const { t } = useTranslation('common')
  const [params] = useSearchParams()
  const studentParam = params.get('student')

  const [me, setMe] = useState(null)
  const [profile, setProfile] = useState(null)
  const [filters, setFilters] = useState({ semester: '', subject: '' })
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const isAdmin = useMemo(() => {
    if (!me) return false
    const roles = me.roles || []
    return roles.includes('admin') || roles.includes('dean') || me.primary_role === 'admin'
  }, [me])

  const studentId = studentParam || profile?.id

  const loadMeAndProfile = async () => {
    try {
      const [m, p] = await Promise.allSettled([
        $api.get('/admin-panel/me/'),
        $api.get('/students/me/'),
      ])
      setMe(m.status === 'fulfilled' ? m.value.data : null)
      setProfile(p.status === 'fulfilled' ? p.value.data : null)
    } catch {

    }
  }

  const loadGradebook = async () => {
    if (!studentId) return
    setLoading(true)
    setLoadError(false)
    try {
      const p = { student: studentId }
      if (filters.semester) p.semester = filters.semester
      if (filters.subject) p.subject = filters.subject
      const r = await $api.get('/gradebook/gradebook/', { params: p })
      setRows(r.data?.results || r.data || [])
    } catch {
      setLoadError(true)
      setNotify({ isOpen: true, message: t('gradebook.load_failed'), type: 'error', sound: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      await loadMeAndProfile()
    })()
  }, [])

  useEffect(() => {
    loadGradebook()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, filters])

  const downloadPdf = async () => {
    try {
      const r = await $api.get(`/gradebook/gradebook/export/`, {
        params: { student: studentId },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([r.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `gradebook_${studentId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {
      setNotify({
        isOpen: true,
        message: t('gradebook.pdf_unavailable'),
        type: 'warning',
        sound: 'warning',
      })
    }
  }

  // suppress unused-warning style: isAdmin retained for future role-based UI
  void isAdmin

  const subtitleText = t('gradebook.student_inline', {
    name: profile?.full_name || me?.full_name || '—',
    group: profile?.group_name || profile?.group?.name || '—',
  })

  const gradeVariant = (val) => {
    const n = Number(val)
    if (Number.isNaN(n)) return 'muted'
    if (n >= 4.5) return 'success'
    if (n >= 3) return 'info'
    if (n > 0) return 'warning'
    return 'muted'
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('gradebook.title')}
        subtitle={subtitleText}
        actions={
          <UnvButton variant="primary" iconLeft="download" onClick={downloadPdf}>
            {t('gradebook.download_pdf')}
          </UnvButton>
        }
      />

      <div className={styles.actions}>
        <UnvInput
          placeholder={t('gradebook.semester_placeholder')}
          value={filters.semester}
          onChange={(e) => setFilters({ ...filters, semester: e.target.value })}
        />
        <UnvInput
          placeholder={t('gradebook.subject_id_placeholder')}
          value={filters.subject}
          onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
        />
      </div>

      {loading ? (
        <Skeleton variant="table" rows={8} cols={6} />
      ) : loadError ? (
        <ErrorState title={t('gradebook.load_failed')} onRetry={loadGradebook} />
      ) : rows.length === 0 ? (
        <EmptyState iconName="folder" title={t('gradebook.no_records')} />
      ) : (
        <DataTable
          columns={[
            { key: 'semester', label: t('gradebook.col_semester'), sortable: true, render: (_, r) => r.semester_name || r.semester || '—' },
            { key: 'subject', label: t('gradebook.col_subject'), sortable: true, render: (_, r) => r.subject_name || r.subject?.name || '—' },
            { key: 'credits', label: t('gradebook.col_credits'), align: 'right', render: (_, r) => r.credits ?? r.ects ?? '—' },
            {
              key: 'grade',
              label: t('gradebook.col_grade'),
              render: (_, r) => {
                const v = r.grade ?? r.value
                return <Badge variant={gradeVariant(v)}>{v ?? '—'}</Badge>
              },
            },
            { key: 'signed_by', label: t('gradebook.col_signed_by'), render: (_, r) => r.signed_by_name || r.signed_by?.full_name || '—' },
            { key: 'date', label: t('gradebook.col_date'), render: (_, r) => fmtDate(r.signed_at || r.date) },
          ]}
          data={rows}
          pageSize={20}
          emptyText={t('gradebook.no_records')}
        />
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default GradebookPage
